
import { Component, Prop, Provide } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import { IFilter, ISorter } from "@/models/tables";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import { DataModules } from "@/store/modules/data";
import {
  IUserGroupsProvider,
  IUserGroupsProviderMethods
} from "@/models/providers/userGroups";
import { CreateElement } from "vue";
import { IUserGroup } from "@/models/userGroups";

@Component
export default class UserGroupsProvider extends mixins(
  FilteringSortingTableStore
) {
  // Props

  @Prop({ default: "UGP" })
  public declare id: string;

  @Prop({ default: false })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public declare manualFilters: Record<string, string | number | boolean>;

  @Prop({ default: () => ({}) })
  public availableFilters!: (vm?: UserGroupsProvider) => IFilter[];

  @Prop({ default: () => () => [] })
  public availableSorters!: (vm?: UserGroupsProvider) => ISorter[];

  // Data

  public dataModule: DataModules = DataModules.USER_GROUPS;

  // Computed

  public get hasUserGroups(): boolean {
    return !!this.table.data.length;
  }

  public get canView() {
    return (
      this.$userCan("get_user_group") && this.$userCan("list_functionalities")
    );
  }

  public get canDelete() {
    return this.$userCan("delete_user_group");
  }

  public get userGroupsAvailableFilters() {
    return this.availableFilters(this);
  }

  public get userGroupsAvailableSorters() {
    return this.availableSorters(this);
  }

  // Life-cycle Methods

  public created() {
    if (!this.$userCan("list_user_groups")) {
      this.loading = false;
      return;
    }
    this.$bus.$on("admin/userGroups/save", this.reloadData);
    this.getData();
  }

  public async reloadData() {
    await this.cancelRequests();
    return this.getData({ ignoreStored: true });
  }

  public viewUserGroup(userGroupCode: string) {
    if (!this.canView) return;
    this.openAddEditModal(userGroupCode);
  }

  public openAddEditModal(userGroupCode: IUserGroup["code"]) {
    this.$router
      .push({
        name: userGroupCode ? "adminUserGroup" : "adminUserGroupAdd",
        params: { userGroupCode }
      })
      .catch(err => err);
  }

  public async deleteUserGroup(userGroupCode: string) {
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: (this.$t("_.user_group") as string).toLowerCase()
          })
        },
        events: {
          confirmed: () => {
            this.table.loading = true;

            return this.$store
              .dispatch(`data/${DataModules.USER_GROUPS}/remove`, {
                id: userGroupCode,
                vm: this
              })
              .then(() => {
                this.$buefy.toast.open({
                  message: this.$t("_.user_group_deleted") as string
                });
                this.getData({ ignoreStored: true });
              })
              .catch(() => {
                this.$buefy.toast.open({
                  message: this.$t("_error.removing_object", {
                    object: (this.$t("_.user_group") as string).toLowerCase()
                  }) as string,
                  type: "is-danger"
                });
                this.table.loading = false;
              })
              .finally(() => confirm.close());
          }
        }
      }
    });
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });
    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.getData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $userGroupsData: this.providerData(),
        $userGroupsMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("userGroupsProviderMethods")
  public get providerMethods(): IUserGroupsProviderMethods {
    return {
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData,
      viewUserGroup: this.viewUserGroup,
      deleteUserGroup: this.deleteUserGroup,
      openAddEditModal: this.openAddEditModal
    };
  }

  @Provide("userGroupsProvider")
  public providerData(): IUserGroupsProvider {
    return {
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      availableFilters: this.userGroupsAvailableFilters,
      availableSorters: this.userGroupsAvailableSorters,
      canDelete: this.canDelete,
      canView: this.canView,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      hasUserGroups: this.hasUserGroups,
      isLoading: this.table.loading,
      limit: this.table.limit,
      page: this.table.page,
      table: this.table,
      total: this.table.total,
      userGroups: this.table.data as IUserGroup[]
    };
  }
}
